import { useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, canales as canalesApi, tenants as tenantsApi } from "../lib/api";
import type {
  ChannelStatusOut,
  ChannelType,
  Tenant,
} from "../lib/api";

type ChannelMeta = {
  key: ChannelType | "whatsapp";
  label: string;
  subtitle: string;
  markCls: string;
  glyph: string;
  description: string;
};

const CANALES: ChannelMeta[] = [
  {
    key: "whatsapp",
    label: "WhatsApp Business",
    subtitle: "Meta Cloud API",
    markCls: "is-whatsapp",
    glyph: "WA",
    description: "Recibe y responde mensajes por WhatsApp.",
  },
  {
    key: "telegram",
    label: "Telegram",
    subtitle: "Bot Telegram",
    markCls: "is-telegram",
    glyph: "TG",
    description: "Bot creado en @BotFather.",
  },
  {
    key: "email",
    label: "Correo institucional",
    subtitle: "SMTP + IMAP",
    markCls: "is-email",
    glyph: "@",
    description: "Conecta el correo institucional del tenant.",
  },
  {
    key: "webchat",
    label: "Chat web",
    subtitle: "Widget integrado",
    markCls: "is-webchat",
    glyph: "WC",
    description: "Formulario en el portal del ciudadano.",
  },
  {
    key: "messenger",
    label: "Facebook Messenger",
    subtitle: "Meta Graph API",
    markCls: "is-messenger",
    glyph: "f",
    description: "Mensajes desde la Página de Facebook.",
  },
  {
    key: "instagram",
    label: "Instagram",
    subtitle: "Meta Graph API",
    markCls: "is-instagram",
    glyph: "IG",
    description: "DMs del perfil oficial.",
  },
];

export function TenantCanalesPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [canales, setCanales] = useState<ChannelStatusOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChannelType | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const refresh = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [t, list] = await Promise.all([
        tenantsApi.get(id),
        canalesApi.list(id),
      ]);
      setTenant(t);
      setCanales(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3000);
  };

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to="/tenants">← Alcaldías</Link>
          {tenant && (
            <>
              {" · "}
              <Link to={`/tenants/${tenant.id}`}>{tenant.nombre}</Link>
              {" · Canales"}
            </>
          )}
        </div>
        <h1 style={{ marginTop: 8 }}>Canales digitales</h1>
        <p className="lede">
          {tenant
            ? `Configuración de canales de ${tenant.nombre}. Como super admin puedes configurar todos los canales, incluidos los de Meta (Messenger, Instagram).`
            : "Cargando…"}
        </p>

        {error && <div className="alert err">{error}</div>}

        <div className="channels-grid">
          {CANALES.map((meta) => {
            if (meta.key === "whatsapp") {
              return <WhatsappCard key="whatsapp" meta={meta} tenantId={id ?? ""} />;
            }
            const row = canales.find((c) => c.channel_type === meta.key);
            return (
              <GenericCard
                key={meta.key}
                meta={meta}
                row={row}
                loading={loading}
                onEdit={() => setEditing(meta.key as ChannelType)}
                onDisable={async () => {
                  if (!id) return;
                  try {
                    await canalesApi.deactivate(id, meta.key as ChannelType);
                    flash("ok", `${meta.label} desactivado`);
                    void refresh();
                  } catch (err) {
                    flash("err", err instanceof ApiError ? err.message : "Error");
                  }
                }}
              />
            );
          })}
        </div>

        {editing && id && (
          <ChannelConfigModal
            tenantId={id}
            tenantSlug={tenant?.slug ?? ""}
            channelType={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              flash("ok", "Canal guardado");
              void refresh();
            }}
            onError={(m) => flash("err", m)}
          />
        )}
      </section>

      {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
    </Shell>
  );
}

function WhatsappCard({ meta, tenantId }: { meta: ChannelMeta; tenantId: string }) {
  return (
    <article className="channel-card">
      <header className="channel-card-head">
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <span className={`channel-card-mark ${meta.markCls}`} aria-hidden="true">
            {meta.glyph}
          </span>
          <div>
            <h2 className="channel-card-title">{meta.label}</h2>
            <div className="channel-card-sub">{meta.subtitle}</div>
          </div>
        </div>
      </header>
      <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>
        Configuración dedicada con flujo completo de Meta Cloud API.
      </p>
      <div className="channel-card-actions">
        <Link to={`/tenants/${tenantId}/whatsapp`} className="btn-outline btn-sm">
          Abrir WhatsApp →
        </Link>
      </div>
    </article>
  );
}

function GenericCard({
  meta,
  row,
  loading,
  onEdit,
  onDisable,
}: {
  meta: ChannelMeta;
  row: ChannelStatusOut | undefined;
  loading: boolean;
  onEdit: () => void;
  onDisable: () => Promise<void>;
}) {
  const status = loading
    ? { cls: "muted", text: "Cargando…" }
    : !row?.configurado
      ? { cls: "muted", text: "Sin configurar" }
      : row.active
        ? { cls: "ok", text: "● Activo" }
        : { cls: "warn", text: "Inactivo" };
  return (
    <article className={`channel-card ${row?.active ? "is-active" : ""}`}>
      <header className="channel-card-head">
        <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
          <span className={`channel-card-mark ${meta.markCls}`} aria-hidden="true">
            {meta.glyph}
          </span>
          <div style={{ minWidth: 0 }}>
            <h2 className="channel-card-title">{meta.label}</h2>
            <div className="channel-card-sub">{meta.subtitle}</div>
          </div>
        </div>
        <span className={`channel-card-status ${status.cls}`}>{status.text}</span>
      </header>
      {row?.configurado && row.display_name ? (
        <div className="channel-card-meta">
          <strong>{row.display_name}</strong>
          {row.external_id && row.external_id !== row.display_name && (
            <>
              <br />
              {row.external_id}
            </>
          )}
        </div>
      ) : (
        <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>{meta.description}</p>
      )}
      <div className="channel-card-actions">
        <button type="button" className="btn-outline btn-sm" onClick={onEdit}>
          {row?.configurado ? "Editar" : "Configurar"}
        </button>
        {row?.configurado && row.active && (
          <button
            type="button"
            className="btn-outline btn-sm"
            onClick={() => void onDisable()}
          >
            Desactivar
          </button>
        )}
      </div>
    </article>
  );
}

// ============ Modal ============

function ChannelConfigModal({
  tenantId,
  tenantSlug,
  channelType,
  onClose,
  onSaved,
  onError,
}: {
  tenantId: string;
  tenantSlug: string;
  channelType: ChannelType;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const meta = CANALES.find((c) => c.key === channelType)!;

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-panel">
        <div className="modal-inner">
          <header className="modal-head">
            <span className={`channel-card-mark ${meta.markCls}`} aria-hidden="true">
              {meta.glyph}
            </span>
            <div className="ttl">
              <span className="eyebrow">Configurar canal</span>
              <h2>{meta.label}</h2>
            </div>
            <button
              type="button"
              className="modal-close"
              aria-label="Cerrar"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
                <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="modal-body">
            {channelType === "telegram" && (
              <TelegramForm
                tenantId={tenantId}
                onSaved={onSaved}
                onError={onError}
                onClose={onClose}
              />
            )}
            {channelType === "email" && (
              <EmailForm
                tenantId={tenantId}
                onSaved={onSaved}
                onError={onError}
                onClose={onClose}
              />
            )}
            {channelType === "webchat" && (
              <WebchatForm
                tenantId={tenantId}
                tenantSlug={tenantSlug}
                onSaved={onSaved}
                onError={onError}
                onClose={onClose}
              />
            )}
            {channelType === "messenger" && (
              <MessengerForm
                tenantId={tenantId}
                onSaved={onSaved}
                onError={onError}
                onClose={onClose}
              />
            )}
            {channelType === "instagram" && (
              <InstagramForm
                tenantId={tenantId}
                onSaved={onSaved}
                onError={onError}
                onClose={onClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

type FormProps = {
  tenantId: string;
  onSaved: () => void;
  onError: (m: string) => void;
  onClose: () => void;
};

function TelegramForm({ tenantId, onSaved, onError, onClose }: FormProps) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const t = token.trim();
    if (!t) {
      onError("Pega el bot token antes de activar");
      return;
    }
    setBusy(true);
    try {
      await canalesApi.upsert(tenantId, "telegram", {
        active: true,
        config: { bot_token: t },
      });
      onSaved();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("TelegramForm submit failed:", err);
      const msg =
        err instanceof ApiError
          ? `${err.message} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "Error desconocido al activar Telegram";
      onError(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p style={{ color: "var(--ink-3)", fontSize: 13.5, marginTop: 0 }}>
        Bot creado en{" "}
        <a href="https://t.me/BotFather" target="_blank" rel="noreferrer">
          @BotFather
        </a>
        . MiPueblo registra el webhook automáticamente.
      </p>
      <div className="form-row">
        <label className="label">Bot Token</label>
        <input
          className="input"
          type="password"
          autoComplete="off"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="123456789:AAH…"
        />
      </div>
      <Actions busy={busy} canSubmit={!!token.trim()} onClose={onClose} onSubmit={submit} label="Activar Telegram" />
    </>
  );
}

function EmailForm({ tenantId, onSaved, onError, onClose }: FormProps) {
  const [enOut, setEnOut] = useState(true);
  const [enIn, setEnIn] = useState(true);
  const [host, setHost] = useState("smtp.gmail.com");
  const [port, setPort] = useState(587);
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [iHost, setIHost] = useState("imap.gmail.com");
  const [iPort, setIPort] = useState(993);
  const [iUser, setIUser] = useState("");
  const [iPass, setIPass] = useState("");
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);

  const outValid = !!host && !!user && !!pass && !!from;
  const inValid = !!iHost && !!iUser && !!iPass;
  const canSubmit = (enOut && outValid) || (enIn && inValid);

  const submit = async () => {
    setBusy(true);
    try {
      const config: Record<string, unknown> = {};
      if (from) config.from_address = from;
      if (enOut) {
        config.outgoing = {
          smtp_host: host,
          smtp_port: port,
          smtp_user: user,
          smtp_password: pass,
          smtp_starttls: true,
        };
      }
      if (enIn) {
        config.incoming = {
          imap_host: iHost,
          imap_port: iPort,
          imap_user: iUser,
          imap_password: iPass,
        };
      }
      await canalesApi.upsert(tenantId, "email", {
        active: true,
        display_name: from || iUser || user,
        config,
      });
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "18px 0 4px",
    fontFamily: "var(--font-mono)",
    fontSize: 11,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
  };

  return (
    <>
      <p style={{ marginTop: 0, fontSize: 13, color: "var(--ink-3)" }}>
        El correo entrante (IMAP) y el saliente (SMTP) son independientes.
        Activa el que necesites — puedes usar solo uno o ambos.
      </p>

      <label style={{ ...toggleStyle, marginTop: 4 }}>
        <input type="checkbox" checked={enOut} onChange={(e) => setEnOut(e.target.checked)} />
        Correo saliente (SMTP) — enviar respuestas
      </label>
      {enOut && (
        <>
          <div className="form-row">
            <label className="label">Dirección visible</label>
            <input
              className="input"
              type="email"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="alcaldia@municipio.gov.co"
            />
          </div>
          <div className="channel-form-grid">
            <div className="form-row"><label className="label">Host</label><input className="input" value={host} onChange={(e) => setHost(e.target.value)} /></div>
            <div className="form-row"><label className="label">Puerto</label><input className="input" type="number" value={port} onChange={(e) => setPort(Number(e.target.value))} /></div>
            <div className="form-row"><label className="label">Usuario</label><input className="input" value={user} onChange={(e) => setUser(e.target.value)} /></div>
            <div className="form-row"><label className="label">Contraseña</label><input className="input" type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} /></div>
          </div>
        </>
      )}

      <label style={toggleStyle}>
        <input type="checkbox" checked={enIn} onChange={(e) => setEnIn(e.target.checked)} />
        Correo entrante (IMAP) — recibir y radicar
      </label>
      {enIn && (
        <div className="channel-form-grid">
          <div className="form-row"><label className="label">Host</label><input className="input" value={iHost} onChange={(e) => setIHost(e.target.value)} /></div>
          <div className="form-row"><label className="label">Puerto</label><input className="input" type="number" value={iPort} onChange={(e) => setIPort(Number(e.target.value))} /></div>
          <div className="form-row"><label className="label">Usuario</label><input className="input" value={iUser} onChange={(e) => setIUser(e.target.value)} /></div>
          <div className="form-row"><label className="label">Contraseña</label><input className="input" type="password" autoComplete="new-password" value={iPass} onChange={(e) => setIPass(e.target.value)} /></div>
        </div>
      )}

      <Actions
        busy={busy}
        canSubmit={canSubmit}
        onClose={onClose}
        onSubmit={submit}
        label="Guardar correo"
      />
    </>
  );
}

function WebchatForm({
  tenantId,
  tenantSlug,
  onSaved,
  onError,
  onClose,
}: FormProps & { tenantSlug: string }) {
  const [welcome, setWelcome] = useState("");
  const [color, setColor] = useState("#C6A35E");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const snippet = buildWidgetSnippet(tenantSlug, color);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onError("No se pudo copiar al portapapeles");
    }
  };
  const submit = async () => {
    setBusy(true);
    try {
      await canalesApi.upsert(tenantId, "webchat", {
        active: true,
        config: { welcome_message: welcome || null, primary_color: color || null },
      });
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="form-row">
        <label className="label">Mensaje de bienvenida</label>
        <input className="input" value={welcome} onChange={(e) => setWelcome(e.target.value)} />
      </div>
      <div className="form-row">
        <label className="label">Color principal</label>
        <input className="input" type="color" value={color} onChange={(e) => setColor(e.target.value)} style={{ width: 80, height: 44, padding: 4 }} />
      </div>
      {tenantSlug && (
        <div className="form-row">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label className="label" style={{ margin: 0 }}>Snippet de instalación</label>
            <button type="button" className="btn-outline btn-sm" onClick={() => void copy()}>
              {copied ? "✓ Copiado" : "Copiar"}
            </button>
          </div>
          <p style={{ color: "var(--ink-3)", fontSize: 12.5, margin: "0 0 8px" }}>
            Pega esto antes de <code>&lt;/body&gt;</code> en el sitio de la alcaldía.
            Renderiza un botón flotante que abre el chat ciudadano.
          </p>
          <textarea
            className="input"
            readOnly
            rows={8}
            value={snippet}
            style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, lineHeight: 1.45 }}
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      )}
      <Actions busy={busy} canSubmit onClose={onClose} onSubmit={submit} label="Guardar chat web" />
    </>
  );
}

function buildWidgetSnippet(slug: string, color: string): string {
  const safeColor = /^#[0-9a-fA-F]{3,8}$/.test(color) ? color : "#C6A35E";
  const url = `https://${slug}.mipuebloapp.com/chat?embed=1`;
  return `<!-- MiPueblo · Chat ciudadano de la alcaldía -->
<script>
(function(){
  var d=document;
  var b=d.createElement("button");
  b.setAttribute("aria-label","Abrir chat de la alcaldía");
  b.innerHTML='<span style="font-size:18px;margin-right:8px">💬</span>Chat alcaldía';
  b.style.cssText="position:fixed;bottom:20px;right:20px;z-index:2147483646;background:${safeColor};color:#fff;border:0;border-radius:999px;padding:12px 20px;font:600 14px system-ui,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,.18);cursor:pointer;display:flex;align-items:center";
  var w=d.createElement("div");
  w.style.cssText="position:fixed;bottom:80px;right:20px;width:380px;height:560px;max-height:80vh;z-index:2147483647;border-radius:12px;overflow:hidden;box-shadow:0 12px 40px rgba(0,0,0,.22);display:none;background:#fff";
  var i=d.createElement("iframe");
  i.src="${url}";
  i.title="Chat de la alcaldía";
  i.setAttribute("sandbox","allow-forms allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin");
  i.setAttribute("referrerpolicy","no-referrer");
  i.style.cssText="width:100%;height:100%;border:0";
  w.appendChild(i);
  var x=d.createElement("button");
  x.setAttribute("aria-label","Cerrar chat");
  x.innerHTML="×";
  x.style.cssText="position:absolute;top:6px;right:8px;background:transparent;color:#fff;border:0;font-size:22px;cursor:pointer;z-index:1";
  w.appendChild(x);
  b.onclick=function(){w.style.display=w.style.display==="block"?"none":"block"};
  x.onclick=function(){w.style.display="none"};
  d.body.appendChild(b); d.body.appendChild(w);
})();
</script>`;
}

function MessengerForm({ tenantId, onSaved, onError, onClose }: FormProps) {
  const [pageId, setPageId] = useState("");
  const [token, setToken] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await canalesApi.upsert(tenantId, "messenger", {
        active: true,
        config: { page_id: pageId, page_access_token: token, app_secret: secret },
      });
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p style={{ color: "var(--ink-3)", fontSize: 13.5, marginTop: 0 }}>
        Configurar la app de Meta Business con permisos pages_messaging +
        pages_show_list. Webhook URL:{" "}
        <code style={{ wordBreak: "break-all" }}>
          https://api.mipuebloapp.com/webhooks/meta/&#123;tenant-slug&#125;/messenger
        </code>
      </p>
      <div className="form-row"><label className="label">Page ID</label><input className="input" value={pageId} onChange={(e) => setPageId(e.target.value)} /></div>
      <div className="form-row"><label className="label">Page Access Token (long-lived)</label><input className="input" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} /></div>
      <div className="form-row"><label className="label">App Secret</label><input className="input" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} /></div>
      <Actions busy={busy} canSubmit={!!pageId && !!token && !!secret} onClose={onClose} onSubmit={submit} label="Activar Messenger" />
    </>
  );
}

function InstagramForm({ tenantId, onSaved, onError, onClose }: FormProps) {
  const [pageId, setPageId] = useState("");
  const [igUserId, setIgUserId] = useState("");
  const [token, setToken] = useState("");
  const [secret, setSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await canalesApi.upsert(tenantId, "instagram", {
        active: true,
        config: {
          page_id: pageId,
          ig_user_id: igUserId,
          page_access_token: token,
          app_secret: secret,
        },
      });
      onSaved();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <p style={{ color: "var(--ink-3)", fontSize: 13.5, marginTop: 0 }}>
        Cuenta IG Business vinculada a una FB Page. Webhook URL:{" "}
        <code style={{ wordBreak: "break-all" }}>
          https://api.mipuebloapp.com/webhooks/meta/&#123;tenant-slug&#125;/instagram
        </code>
      </p>
      <div className="form-row"><label className="label">Page ID (FB Page vinculada)</label><input className="input" value={pageId} onChange={(e) => setPageId(e.target.value)} /></div>
      <div className="form-row"><label className="label">Instagram User ID</label><input className="input" value={igUserId} onChange={(e) => setIgUserId(e.target.value)} /></div>
      <div className="form-row"><label className="label">Page Access Token</label><input className="input" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} /></div>
      <div className="form-row"><label className="label">App Secret</label><input className="input" type="password" autoComplete="off" value={secret} onChange={(e) => setSecret(e.target.value)} /></div>
      <Actions busy={busy} canSubmit={!!pageId && !!igUserId && !!token && !!secret} onClose={onClose} onSubmit={submit} label="Activar Instagram" />
    </>
  );
}

function Actions({
  busy,
  canSubmit,
  onClose,
  onSubmit,
  label,
}: {
  busy: boolean;
  canSubmit: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  label: string;
}) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
      <button type="button" className="btn-outline" onClick={onClose}>
        Cancelar
      </button>
      <button
        type="button"
        className="cta"
        style={{ width: "auto", flex: 1 }}
        disabled={busy || !canSubmit}
        onClick={() => void onSubmit()}
      >
        <span className="cta-inner">
          {busy && <span className="spinner" aria-hidden />}
          {busy ? "Guardando…" : label}
        </span>
      </button>
    </div>
  );
}
